import { useTranslation } from 'react-i18next';
import { Coins, DollarSign, Target, Settings } from 'lucide-react';
import { Label } from '@/components/ui/Label';
import type { DriverPayModel } from '@/lib/types';

interface PayModelSelectorProps {
  value: DriverPayModel;
  onChange: (next: DriverPayModel) => void;
  disabled?: boolean;
}

const PAY_MODELS: { value: DriverPayModel; icon: typeof Coins; color: string }[] = [
  { value: 'trip_only', icon: Coins, color: 'text-cyan-500' },
  { value: 'fixed_only', icon: DollarSign, color: 'text-emerald-500' },
  { value: 'target_with_bonus', icon: Target, color: 'text-amber-500' },
  { value: 'custom', icon: Settings, color: 'text-purple-500' },
];

export function PayModelSelector({ value, onChange, disabled = false }: PayModelSelectorProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-1.5">
      <Label>{t('drivers.field.payModel')}</Label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {PAY_MODELS.map((pm) => {
          const Icon = pm.icon;
          const selected = value === pm.value;
          return (
            <button
              key={pm.value}
              type="button"
              onClick={() => onChange(pm.value)}
              disabled={disabled}
              className={`flex flex-col items-start gap-1 p-3 rounded-md border text-start transition-all ${
                selected
                  ? 'border-gold bg-gold/10'
                  : 'border-border hover:border-muted-foreground/40'
              }`}
            >
              <div className="flex items-center gap-2 w-full">
                <Icon className={`h-5 w-5 ${selected ? pm.color : 'text-muted-foreground'}`} />
                <span className={`text-sm font-medium ${selected ? 'text-foreground' : 'text-muted-foreground'}`}>
                  {t(`drivers.payModel.${pm.value}`)}
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {t(`drivers.payModel.${pm.value}Desc`)}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Live preview of monthly pay calculation.
 * Helps the user understand what they're configuring.
 */
export function PayPreview({
  payModel,
  baseSalary,
  monthlyTarget,
  bonusPerTrip,
}: {
  payModel: DriverPayModel;
  baseSalary: number;
  monthlyTarget?: number | null;
  bonusPerTrip?: number | null;
}) {
  const { t } = useTranslation();

  const formula = (() => {
    const base = baseSalary || 0;
    switch (payModel) {
      case 'trip_only':
        return `${base} + Σ(${t('drivers.preview.tripRate')} × ${t('drivers.preview.trips')})`;
      case 'fixed_only':
        return `${base} ${t('common.sar')}`;
      case 'target_with_bonus':
        if (!monthlyTarget || !bonusPerTrip) {
          return t('drivers.preview.targetMissing');
        }
        return `${base} + max(0, ${t('drivers.preview.trips')} − ${monthlyTarget}) × ${bonusPerTrip}`;
      case 'custom':
        return t('drivers.preview.customManual');
    }
  })();

  return (
    <div className="rounded-md border border-border bg-muted/20 p-3 space-y-1">
      <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
        {t('drivers.preview.formula')}
      </p>
      <p className="text-sm font-mono text-foreground">
        {t('drivers.preview.monthlyPay')} = {formula}
      </p>
    </div>
  );
}
